import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  writeBatch,
  onSnapshot,
  Unsubscribe,
  arrayUnion,
  arrayRemove,
  Timestamp,
  addDoc
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Cart, CartItem, CartValidationResult } from "@/types/cart";
import { convertDoc, isOnline } from "./utils";
import { getProduct } from "./products";
import { getTale } from "./tales";
import { getFragranceProduct } from "@/lib/fragranceService";

// Try both casing options to match what the Flutter app uses
const CARTS_COLLECTION = "carts";
const MOBILE_CARTS_COLLECTION = "Carts"; // Flutter might use this capitalized version

// Get a user's cart - query by userId field instead of document ID
export async function getCart(userId: string): Promise<Cart | null> {
  try {
    // First, try to find an existing cart in either collection
    const standardQuery = query(
      collection(db, CARTS_COLLECTION),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );

    const mobileQuery = query(
      collection(db, MOBILE_CARTS_COLLECTION),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );

    const [standardSnapshot, mobileSnapshot] = await Promise.all([
      getDocs(standardQuery),
      getDocs(mobileQuery)
    ]);

    let cartDoc;
    let collectionName;

    // Use the first active cart found in either collection
    if (!standardSnapshot.empty) {
      cartDoc = standardSnapshot.docs[0];
      collectionName = CARTS_COLLECTION;
    } else if (!mobileSnapshot.empty) {
      cartDoc = mobileSnapshot.docs[0];
      collectionName = MOBILE_CARTS_COLLECTION;
    } else {
      // No existing cart found, create a new one
      const newCartRef = doc(collection(db, CARTS_COLLECTION));
      const cartData = {
        userId,
        items: [],
        status: "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(newCartRef, cartData);
      
      // Fetch the newly created cart
      cartDoc = await getDoc(newCartRef);
      collectionName = CARTS_COLLECTION;
    }

    if (!cartDoc.exists()) {
      throw new Error("Cart not found after creation");
    }

    const data = cartDoc.data();
    
    // If the cart exists but doesn't have items array, check for subcollection
    if (!data.items || !Array.isArray(data.items) || data.items.length === 0) {
      try {
        const itemsFromSubcollection = await getCartItemsFromSubcollection(cartDoc.id);
        if (itemsFromSubcollection.length > 0) {
          const cart: Cart = {
            id: cartDoc.id,
            userId: data.userId,
            items: itemsFromSubcollection,
            status: data.status || "active",
            createdAt: data.createdAt,
            updatedAt: data.updatedAt
          };
          return cart;
        }
      } catch (subcollectionError) {
        console.error("Error checking subcollection:", subcollectionError);
      }
    }
    
    // Create a properly typed Cart object
    const cart: Cart = {
      id: cartDoc.id,
      userId: data.userId,
      items: data.items || [],
      status: data.status || "active",
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
    
    return cart;
  } catch (error) {
    console.error("Error fetching cart:", error);
    throw new Error("Failed to fetch cart");
  }
}

// Check if cart items are stored in a subcollection
async function getCartItemsFromSubcollection(cartId: string): Promise<CartItem[]> {
  const items: CartItem[] = [];
  
  // Try lowercase collection first
  let itemsCollection = collection(db, CARTS_COLLECTION, cartId, "items");
  let querySnapshot = await getDocs(itemsCollection);
  
  // If no items found, try capitalized collection
  if (querySnapshot.empty) {
    itemsCollection = collection(db, MOBILE_CARTS_COLLECTION, cartId, "items");
    querySnapshot = await getDocs(itemsCollection);
  }
  
  querySnapshot.forEach(doc => {
    items.push({
      id: doc.id,
      ...doc.data()
    } as CartItem);
  });
  
  return items;
}

// Create or update a cart
export async function upsertCart(userId: string, cartData: Partial<Omit<Cart, 'userId' | 'createdAt' | 'updatedAt' | 'id'>>): Promise<void> {
  try {
    const cartRef = doc(db, CARTS_COLLECTION, userId);
    const docSnapshot = await getDoc(cartRef);
    
    if (docSnapshot.exists()) {
      await updateDoc(cartRef, {
        ...cartData,
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(cartRef, {
        userId,
        items: [],
        status: "active",
        ...cartData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Error upserting cart:", error);
    throw new Error("Failed to update cart");
  }
}

// Get cart items
export async function getCartItems(userId: string): Promise<CartItem[]> {
  try {
    const cartRef = doc(db, CARTS_COLLECTION, userId);
    const docSnapshot = await getDoc(cartRef);
    
    if (!docSnapshot.exists()) {
      return [];
    }
    
    const cartData = convertDoc<Cart>(docSnapshot);
    return cartData.items || [];
  } catch (error) {
    console.error("Error fetching cart items:", error);
    throw new Error("Failed to fetch cart items");
  }
}

// Generate a unique item ID
function generateCartItemId(productId: string, size: string | null, color: string | null, timestamp: number): string {
  const sizeStr = size || 'default';
  const colorStr = color || 'default';
  return `${productId}_${sizeStr}_${colorStr}_${timestamp}`;
}

// Add item to cart
export async function addCartItem(userId: string, item: Omit<CartItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  try {
    console.log(`Adding item to cart for user ${userId}:`, item);
    
    // Check if this item is a fragrance by examining its properties
    const isFragranceByImage = item.image && (item.image.includes('/fragrances/') || item.image.includes('%2Ffragrances%2F'));
    const isFragranceById = ['wciTw77Dn87hOJOE6wAJ'].includes(item.productId); // Add other known fragrance IDs here
    const isFragranceByName = item.name && (item.name.includes('SEVAN') || item.name.includes('MONUMENT'));
    
    // If this is a fragrance but has the wrong type, correct it
    if ((isFragranceByImage || isFragranceById || isFragranceByName) && item.type !== 'fragrance') {
      console.warn(`Correcting fragrance item type from '${item.type}' to 'fragrance'`);
      console.warn('Item details:', { 
        id: item.productId, 
        name: item.name, 
        type: item.type, 
        image: item.image?.substring(0, 50) + '...' 
      });
      
      // Update the item to have the correct type and category
      item = {
        ...item,
        type: 'fragrance',
        category: 'fragrances'
      };
    }
    
    // Verify the product exists
    let product = null;
    let tale = null;
    let fragrance = null;
    
    if (item.type === 'product' || item.type === 'fullset') {
      product = await getProduct(item.productId);
      if (!product) {
        throw new Error("Product not found");
      }
    } else if (item.type === 'tale') {
      if (item.taleId) {
        tale = await getTale(item.taleId);
        if (!tale) {
          throw new Error("Tale not found");
        }
      } else {
        throw new Error("Tale ID is required for tale items");
      }
    } else if (item.type === 'fragrance') {
      // Verify the fragrance exists
      fragrance = await getFragranceProduct(item.productId);
      if (!fragrance) {
        console.warn(`Fragrance with ID ${item.productId} not found, but allowing cart add anyway`);
        // We'll allow adding to cart even if the fragrance isn't found
        // This ensures compatibility with mobile apps
      } else {
        console.log(`Adding fragrance ${fragrance.name} to cart`);
      }
    }
    
    // Find or create the cart for this user
    let cart = await getCart(userId);
    let cartId: string;
    let useCapitalizedCollection = false;
    
    if (!cart) {
      // No cart exists, create one in the appropriate collection
      // Use the capitalized collection if that's what the mobile app uses
      // You can set a preference here based on your needs
      useCapitalizedCollection = true; // Set to true to match mobile app
      
      const collectionName = useCapitalizedCollection ? MOBILE_CARTS_COLLECTION : CARTS_COLLECTION;
      console.log(`Creating new cart for user ${userId} in ${collectionName}`);
      
      const cartData = {
        userId,
        items: [],
        status: "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      // Add the cart document and get its ID
      const cartRef = await addDoc(collection(db, collectionName), cartData);
      cartId = cartRef.id;
    } else {
      // Use existing cart
      cartId = cart.id || '';
      const cartCollection = cart.id ? await getCartCollectionName(cart.id) : null;
      useCapitalizedCollection = cartCollection === MOBILE_CARTS_COLLECTION;
    }
    
    // Create the cart document reference
    const collectionName = useCapitalizedCollection ? MOBILE_CARTS_COLLECTION : CARTS_COLLECTION;
    const cartRef = doc(db, collectionName, cartId);
    
    const timestamp = Date.now();
    const itemId = generateCartItemId(item.productId, item.size || null, item.color || null, timestamp);
    
    const newItem: CartItem = {
      ...item,
      id: itemId,
      adjustedPrice: item.adjustedPrice || 0,
      basePrice: item.basePrice || 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    
    console.log(`Adding item to cart: ${collectionName}/${cartId}`, newItem);
    
    // Add the item to the cart
    await updateDoc(cartRef, {
      items: arrayUnion(newItem),
      updatedAt: serverTimestamp()
    });
    
    return itemId;
  } catch (error) {
    console.error("Error adding item to cart:", error);
    throw new Error("Failed to add item to cart: " + (error as Error).message);
  }
}

// Find which collection a cart document belongs to
async function getCartCollectionName(cartId: string): Promise<string | null> {
  // Check lowercase collection first
  const cartRef = doc(db, CARTS_COLLECTION, cartId);
  const docSnapshot = await getDoc(cartRef);
  
  if (docSnapshot.exists()) {
    return CARTS_COLLECTION;
  }
  
  // Check capitalized collection
  const mobileCartRef = doc(db, MOBILE_CARTS_COLLECTION, cartId);
  const mobileDocSnapshot = await getDoc(mobileCartRef);
  
  if (mobileDocSnapshot.exists()) {
    return MOBILE_CARTS_COLLECTION;
  }
  
  return null;
}

// Update cart item
export async function updateCartItem(
  userId: string,
  itemId: string,
  updates: Partial<Omit<CartItem, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> {
  try {
    console.log(`Updating cart item ${itemId} for user ${userId}:`, updates);
    
    // Find the cart first
    const cart = await getCart(userId);
    if (!cart || !cart.id) {
      throw new Error("Cart not found");
    }
    
    // Determine which collection the cart is in
    const collectionName = await getCartCollectionName(cart.id);
    if (!collectionName) {
      throw new Error("Cart not found in any collection");
    }
    
    console.log(`Updating cart item in ${collectionName}/${cart.id}`);
    
    // Get the cart document
    const cartRef = doc(db, collectionName, cart.id);
    
    // Find the item to update
    const itemIndex = cart.items.findIndex(item => item.id === itemId);
    if (itemIndex === -1) {
      throw new Error("Cart item not found");
    }
    
    // Update the item
    const updatedItems = [...cart.items];
    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      ...updates,
      updatedAt: Timestamp.now()
    };
    
    // Update the cart
    await updateDoc(cartRef, {
      items: updatedItems,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error updating cart item:", error);
    throw new Error("Failed to update cart item");
  }
}

// Remove item from cart
export async function removeCartItem(userId: string, itemId: string): Promise<void> {
  try {
    console.log(`Removing cart item ${itemId} for user ${userId}`);
    
    // Find the cart first
    const cart = await getCart(userId);
    if (!cart || !cart.id) {
      throw new Error("Cart not found");
    }
    
    // Determine which collection the cart is in
    const collectionName = await getCartCollectionName(cart.id);
    if (!collectionName) {
      throw new Error("Cart not found in any collection");
    }
    
    console.log(`Removing item from cart in ${collectionName}/${cart.id}`);
    
    // Get the cart document
    const cartRef = doc(db, collectionName, cart.id);
    
    // Find the item to remove
    const itemToRemove = cart.items.find(item => item.id === itemId);
    if (!itemToRemove) {
      throw new Error("Cart item not found");
    }
    
    // Remove the item
    await updateDoc(cartRef, {
      items: arrayRemove(itemToRemove),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error removing cart item:", error);
    throw new Error("Failed to remove cart item");
  }
}

// Clear cart
export async function clearCart(userId: string): Promise<void> {
  try {
    console.log(`Clearing cart for user ${userId}`);
    
    // Find the cart first
    const cart = await getCart(userId);
    if (!cart || !cart.id) {
      console.log(`No cart found to clear for user ${userId}`);
      return;
    }
    
    // Determine which collection the cart is in
    const collectionName = await getCartCollectionName(cart.id);
    if (!collectionName) {
      console.log(`No cart found in any collection for user ${userId}`);
      return;
    }
    
    console.log(`Clearing cart in ${collectionName}/${cart.id}`);
    
    // Get the cart document
    const cartRef = doc(db, collectionName, cart.id);
    
    // Empty the items array
    await updateDoc(cartRef, {
      items: [],
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error clearing cart:", error);
    throw new Error("Failed to clear cart");
  }
}

// Subscribe to cart for real-time updates
export function subscribeToCart(
  userId: string,
  onCartChange: (cart: Cart | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  console.log(`Setting up cart subscription for user ${userId}`);
  
  let unsubscribeStandard: Unsubscribe | undefined;
  let unsubscribeMobile: Unsubscribe | undefined;
  let currentCart: Cart | null = null;
  let currentCartId: string | null = null;
  let unsubscribeItemsSubcollection: Unsubscribe | undefined;
  let lastUpdated = 0;
  const updateThrottleMs = 100; // Throttle updates to prevent rapid re-renders
  
  // Function to handle cart updates
  const handleCartUpdate = (cart: Cart | null) => {
    const now = Date.now();
    if (now - lastUpdated > updateThrottleMs) {
      lastUpdated = now;
      onCartChange(cart);
    } else {
      // Schedule a delayed update
      setTimeout(() => {
        lastUpdated = Date.now();
        onCartChange(cart);
      }, updateThrottleMs);
    }
  };

  // Subscribe to both collections
  const standardQuery = query(
    collection(db, CARTS_COLLECTION),
    where('userId', '==', userId),
    where('status', '==', 'active')
  );

  const mobileQuery = query(
    collection(db, MOBILE_CARTS_COLLECTION),
    where('userId', '==', userId),
    where('status', '==', 'active')
  );

  // Subscribe to standard collection
  unsubscribeStandard = onSnapshot(
    standardQuery,
    (querySnapshot) => {
      if (!querySnapshot.empty) {
        const cartDoc = querySnapshot.docs[0];
        const cartData = cartDoc.data();
        const cart: Cart = {
          id: cartDoc.id,
          userId: cartData.userId,
          items: cartData.items || [],
          status: cartData.status || "active",
          createdAt: cartData.createdAt,
          updatedAt: cartData.updatedAt
        };
        
        currentCart = cart;
        currentCartId = cartDoc.id;
        handleCartUpdate(cart);
      }
    },
    onError
  );

  // Subscribe to mobile collection
  unsubscribeMobile = onSnapshot(
    mobileQuery,
    (querySnapshot) => {
      if (!querySnapshot.empty) {
        const cartDoc = querySnapshot.docs[0];
        const cartData = cartDoc.data();
        const cart: Cart = {
          id: cartDoc.id,
          userId: cartData.userId,
          items: cartData.items || [],
          status: cartData.status || "active",
          createdAt: cartData.createdAt,
          updatedAt: cartData.updatedAt
        };
        
        currentCart = cart;
        currentCartId = cartDoc.id;
        handleCartUpdate(cart);
      }
    },
    onError
  );

  // Return cleanup function
  return () => {
    if (unsubscribeStandard) unsubscribeStandard();
    if (unsubscribeMobile) unsubscribeMobile();
    if (unsubscribeItemsSubcollection) unsubscribeItemsSubcollection();
  };
}

// Validate cart (check if full sets and tales are in 1:1 ratio)
export async function validateCart(userId: string): Promise<CartValidationResult> {
  try {
    const cartItems = await getCartItems(userId);
    
    const fullSetCount = cartItems.reduce((count, item) => 
      item.type === 'fullset' ? count + item.quantity : count, 0);
    const taleCount = cartItems.reduce((count, item) => 
      item.type === 'tale' ? count + item.quantity : count, 0);
    
    const isValid = fullSetCount >= taleCount;
    
    return {
      isValid,
      message: isValid ? undefined : "You need at least one full set for each tale in your cart.",
      fullSetCount,
      taleCount
    };
  } catch (error) {
    console.error("Error validating cart:", error);
    throw new Error("Failed to validate cart");
  }
}
